import { CountryCode } from "@tournated-com/ui";

import { ClubReference } from "@/types/club.types";
import { Discipline } from "@/types/discipline.types";
import { OrganizationReference } from "@/types/organization.types";
import { ScopePair } from "@/types/scope.types";
import { Sport, SportRef } from "@/types/sport.types";
import { User, UserReference } from "@/types/user.types";

export type Team = {
  id: string;
  title: string;
  image: string | null;
  country: CountryCode | null;
  createdBy: User | null;
  email: string | null;
  facebookLink: string | null;
  instagramLink: string | null;
  phone: string | null;
  tiktokLink: string | null;
  twitterLink: string | null;
  website: string | null;
  sport: Sport | null;
  discipline: Discipline | null;
  joinedFrom: TeamJoinedFrom | null;
  createdAt: Date | null;
};

/**
 * Flat team-detail domain type backed by the REST `DashboardTeamDetail` DTO.
 * Mirrors {@link Team}'s scalar fields (minus `createdBy`, which the DTO no
 * longer returns) and adds the detail relations.
 */
export type TeamDetail = Omit<Team, "createdBy" | "sport"> & {
  sport: SportRef | null;
  club: ClubReference | null;
  members: TeamMember[];
  invitedMembers: string[];
  isClubForTeamRequired: boolean;
};

export const TeamMemberRoleValues = [
  "captain",
  "coach",
  "doctor",
  "player",
  "playingCaptain",
  "staff",
] as const;

export type TeamMemberRole = (typeof TeamMemberRoleValues)[number];

export type TeamMember = {
  id: string;
  user: UserReference | null;
  teamMemberRole: TeamMemberRole;
};

export const TeamJoinedFromValues = ["web", "mobile", "invite"] as const;

export type TeamJoinedFrom = (typeof TeamJoinedFromValues)[number];

export type TeamListItem = {
  id: string;
  /**
   * Temporary numeric DB id, exposed only to bridge the public-facing team URL
   * (`{clientUrl}/team/{internalId}`) that still takes the numeric id. Deprecated
   * on the wire; remove once the client accepts publicIds.
   */
  internalId: number;
  title: string;
  image: string | null;
  country: CountryCode | null;
  email: string | null;
  phone: string | null;
  createdAt: Date;
  joinedFrom: TeamJoinedFrom | null;
  membersCount: number;
  tournamentsCount: number;
  sport: SportRef | null;
  discipline: Discipline | null;
  club: ClubReference | null;
  organization: OrganizationReference | null;
  /**
   * The team's own caller-visible `(platform, organization)` pair — at most
   * one element (see `ScopePair`); empty when the caller cannot see the pair.
   * Drives the Platform column.
   */
  scopes: ScopePair[];
};

export const TeamExportColumnValues = [
  "title",
  "sport",
  "country",
  "clubId",
  "clubName",
  "captainId",
  "captainFullName",
  "email",
  "phone",
  "website",
  "membersCount",
  "coaches",
  "doctors",
  "players",
  "tournamentsCount",
  "createdByFullName",
  "createdAt",
] as const;

export type TeamExportColumn = (typeof TeamExportColumnValues)[number];

export const TeamsTableColumnValues = [
  "title",
  "club",
  "sport",
  "contact",
  "members",
  "tournaments",
  "platform",
  "organization",
  "joined",
] as const;

export type TeamsTableColumn = (typeof TeamsTableColumnValues)[number];
